import { useState } from "react";
import { Switch } from "@/components/ui/switch";
import { usePlatformSettings } from "@/hooks/usePlatformSettings";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { SANDBOX_MODE_ENABLED } from "@/config/productionMode";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function EnvironmentToggle({ compact = false }: { compact?: boolean }) {
  const { isSandbox, settingsId, loading } = usePlatformSettings();
  const queryClient = useQueryClient();
  const [confirmLive, setConfirmLive] = useState(false);
  const [switching, setSwitching] = useState(false);

  const doSwitch = async (mode: "sandbox" | "live") => {
    if (!settingsId) return;
    setSwitching(true);
    const { error } = await supabase
      .from("platform_settings" as any)
      .update({ environment_mode: mode } as any)
      .eq("id", settingsId);
    if (error) {
      toast.error("Failed to switch environment");
    } else {
      toast.success(`Switched to ${mode === "sandbox" ? "Sandbox" : "Live"} mode`);
      queryClient.invalidateQueries({ queryKey: ["platform_settings"] });
    }
    setSwitching(false);
  };

  const handleToggle = (checked: boolean) => {
    if (checked) {
      // switching to Live — confirm
      setConfirmLive(true);
    } else {
      // switching to Sandbox — immediate
      doSwitch("sandbox");
    }
  };

  // Sandbox environments are disabled in production — no switch is rendered.
  if (!SANDBOX_MODE_ENABLED) return null;
  if (loading) return null;

  return (
    <>
      <div className="flex items-center gap-1.5">
        <span
          className={`h-2 w-2 rounded-full shrink-0 ${
            isSandbox ? "bg-warning" : "bg-success"
          }`}
        />
        {!compact && (
          <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">
            {isSandbox ? "Sandbox" : "Live"}
          </span>
        )}
        <Switch
          checked={!isSandbox}
          onCheckedChange={handleToggle}
          disabled={switching}
          className="scale-75"
        />
      </div>

      <AlertDialog open={confirmLive} onOpenChange={setConfirmLive}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Switch to Live Mode?</AlertDialogTitle>
            <AlertDialogDescription>
              This will connect the platform to production APIs and real funds.
              All admin and user data views will switch to live transactions.
              Make sure all settings are properly configured.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => doSwitch("live")}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Switch to Live
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
